import { FeaturesReady } from '@growthbook/growthbook-react';
import React, { useEffect } from 'react';
import { ChatEmbed } from '../ChatEmbed';
import useHardwareFirstTrialCartCleanup from '../hooks/useHardwareFirstTrialCartCleanup';
import useStoreCartCurrencySync from '../hooks/useStoreCartCurrencySync';
import { useSetTenancyFromURL } from '../hooks/useTenancy';
import { getAxonUserData } from '../lib/analytics/events';
import { getFiGrowthBook } from '../lib/growthbook';
import { initInAppShop } from '../lib/inAppShop';
import { applyQuizPreviewFlagOverrides } from '../lib/quizReferral';
import { typedWindow } from '../lib/Window';
import RescueBanner from '../views/RescueLanding/RescueBanner';
import { AwinClickCapture } from './AwinTracker';
import { FacebookClickCapture } from './FacebookClickCapture';

/**
 * What every document runs once on load, whichever app serves it: legacy mounts it at its root and main in
 * AppProviders, so a page moving to main keeps its attribution and flag setup. It starts the feature load that
 * FeaturesReady waits for, so it sits outside FeaturesReady and ahead of everything that reads a promotion: the
 * in-app marker must drop the apps' `fi_partner` cookie before the first promotion fetch, and the quiz preview's
 * override cookies must exist before loadFeatures applies them.
 */
export function DocumentLandingEffects() {
  useEffect(() => {
    initInAppShop();
    applyQuizPreviewFlagOverrides();
    void getFiGrowthBook().setAttributes();
    void getFiGrowthBook().loadFeatures();
  }, []);

  return (
    <FeaturesReady>
      <AwinClickCapture />
      <FacebookClickCapture />
    </FeaturesReady>
  );
}

/**
 * What every storefront page runs, in legacy's App and in main's AppProviders, rendered where the rescue co-brand
 * banner belongs: under the coupon banner, above the app bar. Legacy renders its whole app only once flags have
 * loaded; main's prerendered document does not wait, so the parts that read flags wait here. That also
 * keeps ChatEmbed, which touches the document while rendering, out of the prerender.
 */
export function StorefrontLandingEffects() {
  useSetTenancyFromURL();

  // The shell creates the pixel only once targeting consent resolves, which can come after this mounts.
  useEffect(() => {
    typedWindow.onTargetingConsent?.(() => {
      typedWindow.axon?.('track', 'page_view', { user_data: getAxonUserData() });
    });
  }, []);

  return (
    <>
      <RescueBanner />
      <FeaturesReady>
        <FlaggedStorefrontEffects />
      </FeaturesReady>
    </>
  );
}

function FlaggedStorefrontEffects() {
  useHardwareFirstTrialCartCleanup();
  useStoreCartCurrencySync();
  return <ChatEmbed />;
}
